import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Shared helpers to keep UI state (tabs, filters, page state) synchronized with
 * the browser URL query string.
 *
 * Why: without this, filters/tabs are lost on refresh, can't be shared via URL,
 * and Back/Forward behaves inconsistently. These hooks mirror the canonical
 * `useSearchParams` idiom already used in `Commerce.tsx`.
 *
 * Prod-safety: this is 100% client-side (react-router). No backend, no SSR, no
 * bearing on AWS/serverless — it only reads/writes `window.location.search`.
 *
 * Conventions:
 * - Writing the `defaultValue` (or an empty string) REMOVES the param so URLs
 *   stay clean and "no filter" == "no param".
 * - `replace: true` (default for filters) avoids spamming Back history on every
 *   keystroke/toggle. Tab switches usually want `replace: false` so Back returns
 *   to the previous tab.
 */

interface UrlStateOptions {
  /** Use history.replace instead of push. Default true. */
  replace?: boolean;
  /**
   * Keep the param in the URL even when its value equals `defaultValue`.
   * Default false (the default value is omitted to keep URLs clean). Set true
   * for segmented controls (e.g. a date-range selector) where every option —
   * including the default — should be visible in the URL for consistency.
   */
  keepDefault?: boolean;
}

/**
 * Sync a single string param with the URL query string.
 */
export function useUrlParam(
  key: string,
  defaultValue = '',
  opts: UrlStateOptions = {},
): [string, (val: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const replace = opts.replace ?? true;
  const keepDefault = opts.keepDefault ?? false;
  const value = searchParams.get(key) ?? defaultValue;

  const setValue = useCallback(
    (val: string) => {
      // Build from the LIVE URL (window.location.search), not react-router's
      // render snapshot, so several URL writes in the same tick — e.g. an async
      // channel_id write racing this mount-materialize — compose instead of
      // clobbering each other (history updates window.location synchronously).
      const next = new URLSearchParams(window.location.search);
      if (val === '') next.delete(key);
      else if (val === defaultValue && !keepDefault) next.delete(key);
      else next.set(key, val);
      setSearchParams(next, { replace });
    },
    [key, defaultValue, replace, keepDefault, setSearchParams],
  );

  // With keepDefault, ensure the param is present in the URL even before the
  // user interacts — materialize the default once on mount (replace history) so
  // an always-visible selection (segmented filters, date ranges) is reflected /
  // shareable from the first load. No-op when the param is already present or
  // the default is empty. Later changes are driven by setValue. Reads the live
  // URL so it doesn't clobber a concurrent write (and vice-versa).
  useEffect(() => {
    if (!keepDefault || !defaultValue) return;
    const next = new URLSearchParams(window.location.search);
    if (next.get(key) !== null) return;
    next.set(key, defaultValue);
    setSearchParams(next, { replace: true });
    // Mount-only: intentionally not re-running on searchParams changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [value, setValue];
}

/**
 * Sync a tab/segment param with the URL. Tab switches push to history by default
 * so Back/Forward navigates between tabs (set replace:true to opt out).
 */
export function useUrlTab(
  defaultValue: string,
  key = 'tab',
  opts: UrlStateOptions = {},
): [string, (val: string) => void] {
  return useUrlParam(key, defaultValue, {
    replace: opts.replace ?? false,
    keepDefault: opts.keepDefault,
  });
}

/**
 * Read/write several URL params at once, useful for a "filters" object.
 * `values` maps param name -> string value; empty/default values are removed.
 * Params not present in `values` are left untouched.
 */
export function useUrlParams(): {
  params: URLSearchParams;
  get: (key: string, fallback?: string) => string;
  set: (values: Record<string, string | null | undefined>, opts?: UrlStateOptions) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const get = useCallback(
    (key: string, fallback = '') => searchParams.get(key) ?? fallback,
    [searchParams],
  );

  const set = useCallback(
    (values: Record<string, string | null | undefined>, opts: UrlStateOptions = {}) => {
      // Build from the live URL so concurrent writes compose (see useUrlParam).
      const nextParams = new URLSearchParams(window.location.search);
      for (const [k, v] of Object.entries(values)) {
        if (v === '' || v === null || v === undefined) nextParams.delete(k);
        else nextParams.set(k, v);
      }
      setSearchParams(nextParams, { replace: opts.replace ?? true });
    },
    [setSearchParams],
  );

  return { params: searchParams, get, set };
}
